/**
 * Aus Zeitplänen werden Fälligkeiten.
 *
 * Diese Funktion ist der Übergang von „ich nehme B12 morgens" zu „am 14. August
 * um 06:00 UTC ist eine Einnahme fällig". Sie läuft im Scheduler (der plant
 * voraus) **und** in der App (die den Tag anzeigt) — also gehört sie hierher und
 * nicht in einen der beiden.
 *
 * Rein und ohne Datenbank: was schon existiert, kommt als Parameter herein.
 */

import { isoDateInZone, wallTimeToUtc } from './time.js';
import type { IsoDate } from './time.js';
import type { Schedule } from './reminders.js';
import { describeAmount } from './supplements.js';

export interface PlannableSupplement {
  id: string;
  name: string;
  dose: number | null;
  unit: string | null;
  active: boolean;
  schedule: Schedule;
}

export interface PlannedIntake {
  supplementId: string;
  supplementName: string;
  /** Fälligkeit als Zeitpunkt in UTC. */
  dueAt: Date;
  /** Kurztext für die Benachrichtigung, z. B. „Vitamin B12 · 250 µg". */
  title: string;
}

/** Was der Aufrufer schon in der Datenbank hat: Schlüssel `<supplementId>@<ISO-Zeit>`. */
export function intakeKey(supplementId: string, dueAt: Date): string {
  return `${supplementId}@${dueAt.toISOString()}`;
}

export function describeDose(supplement: PlannableSupplement): string {
  if (supplement.dose === null) return supplement.name;
  // Einheiten sind seit 16.10.2026 Schlüssel („piece“) — der Text liest „1 Stück“.
  return `${supplement.name} · ${describeAmount(supplement.dose, supplement.unit)}`;
}

export interface PlanOptions {
  supplements: readonly PlannableSupplement[];
  /** Zeitzone des Nutzers — Fälligkeiten entstehen in Ortszeit. */
  timeZone: string;
  /** Ab wann geplant wird (meist „jetzt"). */
  from: Date;
  /** Wie weit vorausgeplant wird. */
  horizonDays: number;
  /** Bereits vorhandene Einnahmen, als Schlüssel aus `intakeKey`. */
  existing: ReadonlySet<string>;
}

/**
 * Alle Einnahmen, die im Zeitfenster fällig werden und noch nicht existieren.
 *
 * **Vergangenes wird nicht nachgeplant.** Wer die App nach zwei Wochen Pause
 * öffnet, soll nicht mit vierzehn Tagen versäumter Einnahmen begrüßt werden — das
 * ist kein Hinweis mehr, sondern eine Anklage, und die Liste wäre unbrauchbar.
 * Geplant wird ab `from`, nicht ab dem Tagesbeginn.
 */
export function planIntakes(options: PlanOptions): PlannedIntake[] {
  const { supplements, timeZone, from, horizonDays, existing } = options;
  const planned: PlannedIntake[] = [];

  const startDate = isoDateInZone(from, timeZone);

  for (const supplement of supplements) {
    if (!supplement.active) continue;

    for (let offset = 0; offset <= horizonDays; offset += 1) {
      const date = shiftDate(startDate, offset);
      for (const dueAt of dueTimesForDate(supplement.schedule, date, timeZone)) {
        if (dueAt.getTime() < from.getTime()) continue;
        if (existing.has(intakeKey(supplement.id, dueAt))) continue;

        planned.push({
          supplementId: supplement.id,
          supplementName: supplement.name,
          dueAt,
          title: describeDose(supplement),
        });
      }
    }
  }

  return planned.sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
}

function shiftDate(date: IsoDate, days: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function dueTimesForDate(schedule: Schedule, date: IsoDate, timeZone: string): Date[] {
  if (schedule.months && schedule.months.length > 0) {
    const month = Number(date.slice(5, 7));
    if (!schedule.months.includes(month)) return [];
  }

  if (schedule.weekdays && schedule.weekdays.length > 0) {
    const noon = wallTimeToUtc(date, '12:00', timeZone);
    const weekday = isoWeekdayOf(noon, timeZone);
    if (!schedule.weekdays.includes(weekday)) return [];
  }

  return schedule.times.map((time) => wallTimeToUtc(date, time, timeZone));
}

function isoWeekdayOf(instant: Date, timeZone: string): number {
  const short = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(instant);
  const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const index = order.indexOf(short);
  return index === -1 ? 1 : index + 1;
}
